import { Helmet } from "react-helmet-async";
import { Trans, useTranslation } from "react-i18next";
import "./MyJourney.css";

/* ----------------------------------------------------------------
   Reveal-on-scroll wrapper for elegant animated transitions
----------------------------------------------------------------- */
function Reveal({ children, className = "" }) {
  return <div className={className}>{children}</div>;
}

/* ----------------------------------------------------------------
  Editorial section heading - left aligned with number + eyebrow
----------------------------------------------------------------- */
function SectionHeading({ number, eyebrow, title }) {
  return (
    <div className="jrr-section-heading mb-8">
      <div className="mb-5 flex items-center gap-3 sm:mb-7 sm:gap-4">
        {number && (
          <span className="font-['Noto_Serif',serif] text-sm font-semibold tracking-widest text-[#b57a3a]">
            {number}
          </span>
        )}
        <span className="h-px w-10 bg-[#b57a3a]/40" />
        {eyebrow && (
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#b57a3a]">
            {eyebrow}
          </p>
        )}
      </div>
      <h2 className="font-['Noto_Serif',serif] text-3xl font-normal leading-tight tracking-normal text-[#1a1712] sm:text-4xl md:text-[2.75rem]">
        {title}
      </h2>
    </div>
  );
}
function Quote({ children }) {
  return (
    <blockquote className="jrr-quote relative my-8 border-l-2 border-[#b57a3a] bg-[#fff8ef] py-7 pl-6 pr-5 sm:py-9 sm:pl-8 sm:pr-6 md:my-8 md:py-10 md:pl-10 md:pr-10">
      <span className="pointer-events-none absolute -top-4 left-6 font-['Noto_Serif',serif] text-6xl leading-none text-[#b57a3a]/25">
        &ldquo;
      </span>
      <p className="font-['Noto_Serif',serif] text-xl font-normal italic leading-relaxed text-[#2b2620] md:text-2xl">
        {children}
      </p>
    </blockquote>
  );
}

function Prose({ children }) {
  return (
    <div className="jrr-prose space-y-8 text-[1.0625rem] leading-[1.95] text-[#4a453c] md:space-y-10 md:text-lg md:leading-[2]">
      {children}
    </div>
  );
}

/* Thin editorial divider between story chapters */
function ChapterDivider() {
  return (
    <div className="jrr-chapter-divider my-16 flex items-center gap-5 md:my-12">
      <span className="h-px flex-1 bg-[#e2d7c3]" />
      <span className="h-1.5 w-1.5 rotate-45 bg-[#b57a3a]/60" />
      <span className="h-px flex-1 bg-[#e2d7c3]" />
    </div>
  );
}

export default function MyJourney() {
  const { t } = useTranslation();
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="jrr-page min-h-screen bg-white font-sans text-[#202122]">
      <Helmet>
        <title>Harisharan Devgan's Journey | Life, Ideology &amp; Achievements</title>
        <meta
          name="description"
          content="Explore the journey of Harisharan Devgan, from his roots in agriculture and organic farming to his involvement in social welfare, public life and politics."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://harisharandevgan.com/journey/" />

        <meta
          property="og:title"
          content="Harisharan Devgan's Journey | Life, Ideology &amp; Achievements"
        />
        <meta
          property="og:description"
          content="Explore the journey of Harisharan Devgan, from his roots in agriculture and organic farming to his involvement in social welfare, public life and politics."
        />
        <meta property="og:url" content="https://harisharandevgan.com/journey/" />
        <meta property="og:site_name" content="Harisharan Devgan" />
        <meta property="og:updated_time" content="2026-02-01T11:53:28+05:30" />
        <meta
          property="og:image"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />
        <meta
          property="og:image:secure_url"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="576" />
        <meta property="og:image:alt" content="Connect" />
        <meta property="og:image:type" content="image/webp" />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Harisharan Devgan's Journey | Life, Ideology &amp; Achievements"
        />
        <meta
          name="twitter:description"
          content="Explore the journey of Harisharan Devgan, from his roots in agriculture and organic farming to his involvement in social welfare, public life and politics."
        />
        <meta
          name="twitter:image"
          content="https://harisharandevgan.com/images/harisharan-devgan-800.webp"
        />
        <meta name="twitter:label1" content="Time to read" />
        <meta name="twitter:data1" content="6 minutes" />

        {/* Favicon / Icons */}
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon//favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon//favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicon//apple-touch-icon.png" />
        <link rel="manifest" href="/favicon//site.webmanifest" />
        <link rel="shortcut icon" href="/favicon/favicon.ico" />
      </Helmet>

      <main className="jrr-main">
        <article className="jrr-article">
          <header
            id="home"
            className="relative flex min-h-[500px] max-h-[600px] items-center overflow-hidden bg-[#0b0908] bg-cover bg-center"
            style={{
              backgroundImage:
                "linear-gradient(90deg, rgba(8,7,6,0.92) 0%, rgba(8,7,6,0.72) 38%, rgba(8,7,6,0.30) 70%, rgba(8,7,6,0.08) 100%), url('/images/gallery/harisharan_devgan_39.webp')",
            }}
          >
            {/* Cinematic overlay */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-black/10" />

            {/* Subtle frame */}
            <div className="pointer-events-none absolute inset-4 border border-white/10 md:inset-6 lg:inset-8" />

            <div className="relative z-10 mx-auto w-full max-w-[1500px] px-6 py-10 md:px-14 md:py-12 lg:px-20 lg:py-16 xl:px-24">
              <div className="max-w-4xl">

                {/* Eyebrow */}
                <div className="mb-5 flex items-center gap-3 sm:gap-4">

                  <p className="m-0 whitespace-nowrap text-[10px] font-medium uppercase tracking-[0.38em] text-[#E8780C] md:text-[11px]">
                    {t("jrr.pageHero.eyebrow")}
                  </p>

                </div>

                {/* Main title */}
                <h1 className="m-0 text-white">
                  <span className="block text-[clamp(2.5rem,5vw,6rem)] font-light leading-[0.9] tracking-[-0.055em] text-white">
                    {t("jrr.pageHero.name")}
                  </span>
                </h1>

                {/* Subtitle */}
                <div className="mt-6 flex items-center gap-3">
                  <span aria-hidden="true" className="h-px w-7 shrink-0 bg-gradient-to-r from-transparent via-[#E8780C]/60 to-[#E8780C]" />

                  <p className="m-0 whitespace-nowrap text-xs font-semibold uppercase tracking-[0.2em] text-[#E8780C] md:text-sm">
                    {t("jrr.pageHero.subtitle")}
                  </p>

                  <span aria-hidden="true" className="h-px w-7 shrink-0 bg-gradient-to-l from-transparent via-[#E8780C]/60 to-[#E8780C]" />
                </div>

                {/* Description */}
                <p className="mt-5 max-w-2xl text-sm font-light leading-[1.75] tracking-[0.01em] text-white/75 md:text-base lg:text-[17px]">
                  {t("jrr.pageHero.description")}
                </p>

                {/* CTA */}
                <button
                  type="button"
                  onClick={() => scrollToSection("roots")}
                  className="group mt-7 inline-flex items-center gap-4 border border-[#E8780C]/70 bg-[#E8780C] px-5 py-3.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-white shadow-[0_8px_30px_rgba(232,120,12,0.20)] transition-all duration-500 hover:bg-[#d96d08] hover:shadow-[0_10px_35px_rgba(232,120,12,0.32)]"
                >
                  <span>{t("jrr.pageHero.cta")}</span>

                  <span className="flex h-7 w-7 items-center justify-center border border-white/40 transition-all duration-500 group-hover:translate-x-1">
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      className="h-3.5 w-3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path
                        d="M5 12h13M13 6l6 6-6 6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
              </div>
            </div>

            {/* Side line */}
            <div className="absolute bottom-0 right-8 top-0 hidden w-px bg-white/[0.08] lg:block" />
          </header>

          <div className="journey-story-content jrr-story-content">
            <section id="roots" className="scroll-mt-24">
              <Reveal>
                <SectionHeading
                  number="01"
                  eyebrow={t("jrr.pageRoots.eyebrow")}
                  title={t("jrr.pageRoots.title")}
                />
              </Reveal>
              <Reveal>
                <Prose>
                  <p className="font-['Noto_Serif',serif] text-xl font-normal leading-9 tracking-normal text-[#2b2620] md:text-2xl">
                    <Trans
                      i18nKey="jrr.pageRoots.p1"
                      components={{
                        personName: <span className="font-semibold text-black" />,
                        date: <span className="font-semibold text-black" />,
                        roots: <span />,
                      }}
                    />
                  </p>
                  <p>
                    <Trans
                      i18nKey="jrr.pageRoots.p2"
                      components={{
                        father: <span className="font-semibold text-black" />,
                        mother: <span className="font-semibold text-black" />,
                        sister: <span className="font-semibold text-black" />,
                      }}
                    />
                  </p>
                  <p>
                    <Trans
                      i18nKey="jrr.pageRoots.p3"
                      components={{ agriculture: <span /> }}
                    />
                  </p>
                  <p>
                    <Trans
                      i18nKey="jrr.pageRoots.p4"
                      components={{
                        spouse: <span className="font-semibold text-black" />,
                        son1: <span className="font-semibold text-black" />,
                        son2: <span className="font-semibold text-black" />,
                      }}
                    />
                  </p>
                  <p>
                    {t("jrr.pageRoots.p5")}
                  </p>
                  <p>{t("jrr.pageRoots.p6")}</p>
                </Prose>
              </Reveal>
              <Reveal>
                <Quote>{t("jrr.pageRoots.quote")}</Quote>
              </Reveal>
              <Reveal>
                <Prose>
                  <p>
                    {t("jrr.pageRoots.p7")}
                  </p>
                </Prose>
              </Reveal>
            </section>

            <ChapterDivider />

            <section id="belief" className="scroll-mt-24">
              <Reveal>
                <SectionHeading
                  number="02"
                  eyebrow={t("jrr.pageBelief.eyebrow")}
                  title={t("jrr.pageBelief.title")}
                />
              </Reveal>

              <div className="jrr-side-image-layout">
                <div className="jrr-side-image-copy">
                  <Reveal>
                    <Prose>
                      <p>
                        {t("jrr.pageBelief.p1")}
                      </p>
                      <p>{t("jrr.pageBelief.p2")}</p>
                      <p>{t("jrr.pageBelief.p3")}</p>
                    </Prose>
                  </Reveal>
                  <Reveal>
                    <Quote>{t("jrr.pageBelief.quote")}</Quote>
                  </Reveal>
                  <Reveal>
                    <Prose>
                      <p>{t("jrr.pageBelief.p4")}</p>
                      <p>
                        {t("jrr.pageBelief.p5")}
                      </p>
                    </Prose>
                  </Reveal>
                </div>

                <div className="jrr-side-image-wrap">
                  <figure className="jrr-side-image">
                    <img
                      src="/images/gallery/harisharan-devgan-with-farmers.webp"
                      alt={t("jrr.pageBelief.imageAlt")}
                    />
                  </figure>
                </div>
              </div>
            </section>

            <ChapterDivider />

            <section id="vision" className="scroll-mt-24">
              <Reveal>
                <SectionHeading
                  number="03"
                  eyebrow={t("jrr.pageVision.eyebrow")}
                  title={t("jrr.pageVision.title")}
                />
              </Reveal>
              <Reveal>
                <Prose>
                  <p className="font-['Noto_Serif',serif] text-xl font-normal leading-9 tracking-normal text-[#2b2620] md:text-2xl">
                    {t("jrr.pageVision.p1")}
                  </p>
                  <p>
                    {t("jrr.pageVision.p2")}
                  </p>
                  <p>
                    {t("jrr.pageVision.p3")}
                  </p>
                  <p>
                    <Trans
                      i18nKey="jrr.pageVision.p4"
                      components={{ perseverance: <span /> }}
                    />
                  </p>
                  <p>
                    {t("jrr.pageVision.p5")}
                  </p>
                  <p>
                    <Trans
                      i18nKey="jrr.pageVision.p6"
                      components={{
                        award: <span className="font-semibold text-black" />,
                      }}
                    />
                  </p>
                  <p>
                    {t("jrr.pageVision.p7")}
                  </p>


                </Prose>
              </Reveal>
            </section>

            <ChapterDivider />

            <section id="families" className="scroll-mt-24">
              <Reveal>
                <SectionHeading
                  number="04"
                  eyebrow={t("jrr.pageFamilies.eyebrow")}
                  title={t("jrr.pageFamilies.title")}
                />
              </Reveal>
              <Reveal>
                <Prose>
                  <p>
                    {t("jrr.pageFamilies.p1")}
                  </p>

                  <p className="jrr-callout-line border-l-2 border-[#b57a3a] pl-5 font-['Noto_Serif',serif] text-2xl font-normal text-[#b57a3a] md:text-3xl">
                    {t("jrr.pageFamilies.callout")}
                  </p>
                  <p>
                    <Trans
                      i18nKey="jrr.pageFamilies.p2"
                      components={{ scholarships: <span /> }}
                    />
                  </p>
                  <p>
                    {t("jrr.pageFamilies.p3")}
                  </p>
                  <p>
                    {t("jrr.pageFamilies.p4")}
                  </p>
                </Prose>
                <figure className="jrr-full-width-image jrr-vision-image mt-8">
                  <img
                    src="/images/harisharan-devgan-sharing-a-meal-with-schoolchildren.webp"
                    alt={t("jrr.pageFamilies.imageAlt")}
                  />
                </figure>
              </Reveal>
            </section>

            <ChapterDivider />

            <section id="standing" className="scroll-mt-24">
              <Reveal>
                <SectionHeading
                  number="05"
                  eyebrow={t("jrr.pageStanding.eyebrow")}
                  title={t("jrr.pageStanding.title")}
                />
              </Reveal>
              <Reveal>
                <Prose>
                  <p>
                    {t("jrr.pageStanding.p1")}
                  </p>
                  <p>
                    {t("jrr.pageStanding.p2")}
                  </p>
                  <p>
                    {t("jrr.pageStanding.p3")}
                  </p>
                  <p>
                    <Trans
                      i18nKey="jrr.pageStanding.p4"
                      components={{ voice: <span /> }}
                    />
                  </p>
                </Prose>
                <Prose>
                  <p>
                    <Trans
                      i18nKey="jrr.pageStanding.p5"
                      components={{
                        msp: <span className="font-semibold text-black" />,
                      }}
                    />
                  </p>
                  <p className="mt-4 mb-4 font-['Noto_Serif',serif] text-2xl font-normal leading-9 tracking-normal text-[#b57a3a] md:text-3xl">
                    {t("jrr.pageStanding.p6")}
                  </p>
                  <p>
                    {t("jrr.pageStanding.p7")}
                  </p>
                  <p>
                    <Trans
                      i18nKey="jrr.pageStanding.p8"
                      components={{ concerns: <span /> }}
                    />
                  </p>
                  <p>
                    {t("jrr.pageStanding.p9")}
                  </p>
                </Prose>
              </Reveal>
            </section>

            <ChapterDivider />

            <section id="students" className="scroll-mt-24">
              <Reveal>
                <SectionHeading
                  number="06"
                  eyebrow={t("jrr.pageStudents.eyebrow")}
                  title={t("jrr.pageStudents.title")}
                />
              </Reveal>
              <Reveal>
                <Prose>
                  <p>
                    {t("jrr.pageStudents.p1")}
                  </p>
                  <p>
                    {t("jrr.pageStudents.p2")}
                  </p>
                  <p>
                    <Trans
                      i18nKey="jrr.pageStudents.p3"
                      components={{
                        campaign: (
                          <a
                            href="https://harisharandevgan.com/blog/jharkhand-student-unity/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-black underline decoration-black underline-offset-4"
                          />
                        ),
                      }}
                    />
                  </p>
                  <p>
                    <Trans
                      i18nKey="jrr.pageStudents.p4"
                      components={{ examination: <span /> }}
                    />
                  </p>
                  <p>
                    {t("jrr.pageStudents.p5")}
                  </p>
                </Prose>
              </Reveal>

              <Reveal>
                <figure className="jrr-full-width-image">
                  <img
                    src="/images/harisharan-devgan-jpsc.webp"
                    alt={t("jrr.pageStudents.imageAlt")}
                  />
                </figure>
              </Reveal>
            </section>

            <ChapterDivider />
           
            <section id="supreme-court" className="scroll-mt-24">
              <Reveal>
                <SectionHeading number="07" eyebrow={t("jrr.pageSupremeCourt.eyebrow")} title={t("jrr.pageSupremeCourt.title")} />
              </Reveal>
              <Reveal>
                <Prose>
                  <p>
                    {t("jrr.pageSupremeCourt.p1")}
                    {" "}
                    <Trans
                      i18nKey="jrr.pageSupremeCourt.p2"
                      components={{
                        case: <span className="font-semibold text-black" />,
                        year: <span className="font-semibold text-black" />,
                        name: <span className="font-semibold text-black" />,
                        neet: <span className="font-semibold text-black" />,
                        person: (
                          <a
                            href="https://harisharandevgan.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-black underline decoration-black underline-offset-4"
                          />
                        ),
                        neetIssue: <span />,
                      }}
                    />
                  </p>
                </Prose>
                <div className="jrr-side-image-layout jrr-public-interest-image-layout mt-8">
                  <div className="jrr-side-image-copy">
                    <Prose>
                      <p>
                        {t("jrr.pageSupremeCourt.p3")}
                        {" "}
                        {t("jrr.pageSupremeCourt.p4")}
                        {" "}
                        {t("jrr.pageSupremeCourt.p5")}
                      </p>
                      <p>
                        {t("jrr.pageSupremeCourt.p6")}
                      </p>
                      <p>
                        <Trans
                          i18nKey="jrr.pageSupremeCourt.p7"
                          components={{ fairHearing: <span /> }}
                        />
                      </p>
                    </Prose>
                  </div>
                  <div className="jrr-side-image-wrap">
                    <figure className="jrr-side-image">
                      <video
                        src="/video/neet-pg.mp4"
                        poster="/images/neet26.jpg"
                        controls
                        playsInline
                        preload="metadata"
                        aria-label={t("jrr.pageSupremeCourt.videoAlt")}
                      />
                    </figure>
                  </div>
                </div>
              </Reveal>
            </section>

            <ChapterDivider />

            <section id="political-voice" className="scroll-mt-24">
              <Reveal>
                <SectionHeading number="08" eyebrow={t("jrr.pagePoliticalVoice.eyebrow")} title={t("jrr.pagePoliticalVoice.title")} />
              </Reveal>
              <Reveal>
                <Prose>
                  <p>
                    {t("jrr.pagePoliticalVoice.p1")}
                  </p>
                  <p>
                    {t("jrr.pagePoliticalVoice.p2")}
                  </p>
                  
                  <p>
                    <Trans
                      i18nKey="jrr.pagePoliticalVoice.p4"
                      components={{
                        identity: (
                          <a
                            href="https://harisharandevgan.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold text-black underline decoration-black underline-offset-4"
                          />
                        ),
                      }}
                    />
                  </p>
                  <p>
                    {t("jrr.pagePoliticalVoice.p5")}
                  </p>
                </Prose>
              </Reveal>
            </section>

            <ChapterDivider />

            <section id="beyond-agriculture" className="scroll-mt-24">
              <Reveal>
                <SectionHeading number="09" eyebrow={t("jrr.pageBeyondAgriculture.eyebrow")} title={t("jrr.pageBeyondAgriculture.title")} />
              </Reveal>
              <Reveal>
                <div className="grid grid-cols-1 gap-1.5 lg:grid-cols-[max-content_minmax(0,1fr)] lg:items-start lg:gap-10">
                  <div className="flex flex-col items-start gap-2.5 lg:pt-[0.1rem]">
                    <figure className="m-0 overflow-hidden rounded-full border border-[#d9c7a3] bg-gradient-to-br from-[#fffaf1] via-[#f5efe8] to-[#efe4d0] p-[3px] shadow-[0_0_0_1px_rgba(181,122,58,0.18),0_14px_20px_rgba(45,34,23,0.08)]">
                      <img src="/images/company-logos/niche-clean-energy.webp" alt={t("jrr.pageBeyondAgriculture.cleanEnergyAlt")} className="h-20 w-20 rounded-full object-cover sm:h-[5.5rem] sm:w-[5.5rem]" />
                    </figure>
                    <figure className="m-0 overflow-hidden rounded-full border border-[#d9c7a3] bg-gradient-to-br from-[#fffaf1] via-[#f5efe8] to-[#efe4d0] p-[3px] shadow-[0_0_0_1px_rgba(181,122,58,0.18),0_14px_20px_rgba(45,34,23,0.08)]">
                      <img src="/images/company-logos/niche-film-farm.webp" alt={t("jrr.pageBeyondAgriculture.filmFarmAlt")} className="h-20 w-20 rounded-full object-cover sm:h-[5.5rem] sm:w-[5.5rem]" />
                    </figure>
                    <figure className="m-0 overflow-hidden rounded-full border border-[#d9c7a3] bg-gradient-to-br from-[#fffaf1] via-[#f5efe8] to-[#efe4d0] p-[3px] shadow-[0_0_0_1px_rgba(181,122,58,0.18),0_14px_20px_rgba(45,34,23,0.08)]">
                      <img src="/images/company-logos/niche-racing.webp" alt={t("jrr.pageBeyondAgriculture.racingAlt")} className="h-20 w-20 rounded-full object-cover sm:h-[5.5rem] sm:w-[5.5rem]" />
                    </figure>
                  </div>

                  <div className="space-y-8">
                    <Prose>
                      <p>
                        <Trans
                          i18nKey="jrr.pageBeyondAgriculture.p1"
                          components={{
                            cleanEnergy: (
                              <a
                                href="https://nichecleanenergy.com/"
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                className="font-semibold text-black underline decoration-black underline-offset-4"
                              />
                            ),
                          }}
                        />
                      </p>
                      <p>
                        <Trans
                          i18nKey="jrr.pageBeyondAgriculture.p2"
                          components={{
                            filmFarm: (
                              <a
                                href="https://nichefilmfarm.com/"
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                className="font-semibold text-black underline decoration-black underline-offset-4"
                              />
                            ),
                          }}
                        />
                      </p>
                    </Prose>
                    <Prose>
                      <p className="jrr-horses-paragraph">
                        <Trans
                          i18nKey="jrr.pageBeyondAgriculture.p3"
                          components={{
                            equestrianism: <span />,
                            racing: (
                              <a
                                href="https://nicheracing.com/"
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                className="font-semibold text-black underline decoration-black underline-offset-4"
                              />
                            ),
                          }}
                        />
                      </p>
                    </Prose>
                  </div>
                </div>
              </Reveal>
            </section>

            <ChapterDivider />

            <section id="journey-ahead" className="scroll-mt-24">
              <Reveal>
                <SectionHeading number="10" eyebrow={t("jrr.pageJourneyAhead.eyebrow")} title={t("jrr.pageJourneyAhead.title")} />
              </Reveal>
              <Reveal>
                <Prose>
                  <p>
                    {t("jrr.pageJourneyAhead.p1")}
                  </p>
                  <Quote>{t("jrr.pageJourneyAhead.quote")}</Quote>
                  <p>
                    {t("jrr.pageJourneyAhead.p2")}
                  </p>
                  <p>
                    {t("jrr.pageJourneyAhead.p3")}
                  </p>
                  <div
                    className="jrr-signoff"
                    style={{
                      backgroundImage:
                        "url('/images/gallery/harisharan-devgan-farmer-agriculture-discussion.webp')",
                      backgroundPosition: "center -100px",
                    }}
                  >
                    <div className="jrr-signoff-content">
                      <div className="jrr-signoff-meta">
                        <p className="jrr-signoff-name custom">{t("jrr.pageJourneyAhead.signoffName")}</p>
                        <p className="jrr-signoff-role custom">{t("jrr.pageJourneyAhead.signoffRole")}</p>
                      </div>
                    </div>
                  </div>
                </Prose>
              </Reveal>
            </section>
          </div>
        </article>
      </main>
    </div>
  );
}